import {
  type DashboardSalesPaymentRow,
  DEFAULT_SYSTEM_SETTINGS,
  formatBaht,
  LEGACY_MANUAL_CHANNEL_ID,
  LEGACY_MANUAL_PAYMENT_LABEL,
  type PaymentMethodCode,
  type PermissionAction,
  type ProductCatalogItem,
  type Promotion,
  resolvePermissionOverride,
  type Role,
  type SystemSettings,
  type UserAccount,
} from '@pos/types';

export type PermissionDenialReason =
  'user_missing' | 'override_denied' | 'role_disallowed';

export interface PermissionResult {
  allowed: boolean;
  cap?: number;
  requireApproval?: boolean;
  reason?: PermissionDenialReason;
}

export interface VatBreakdown {
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
}

export interface BillRoundingResult {
  roundedTotal: number;
  roundingDelta: number;
}

export type ReceiptLocale = 'th' | 'en';

export interface ReceiptHeaderTemplate {
  readonly titlePrimary: string;
  readonly titleSecondary: string;
  readonly logoLine: string;
  readonly shopLine: string;
  readonly branchLine: string;
  readonly addressLines: readonly string[];
  readonly taxLine: string;
  readonly phoneLine?: string;
}

export interface ReceiptFooterTemplate {
  readonly lines: readonly string[];
}

export interface ReceiptReprintTemplate {
  readonly bannerTemplate: string;
  readonly lineTemplate: string;
}

export interface ReceiptTemplate {
  readonly locale: ReceiptLocale;
  readonly width: number;
  readonly dividerChar: string;
  readonly header: ReceiptHeaderTemplate;
  readonly footer: ReceiptFooterTemplate;
  readonly labels: {
    readonly receiptNoLabel: string;
    readonly dateLabel: string;
    readonly timeLabel: string;
    readonly cashierLabel: string;
    readonly subtotalLabel: string;
    readonly vatLabel: string;
    readonly totalLabel: string;
    readonly paymentLabels: Record<PaymentMethodCode, string>;
    readonly changeLabel: string;
    readonly evidenceLabel: string;
    readonly gatewayPaymentIdLabel: string;
    readonly settlementLabel: string;
  };
  readonly reprint: ReceiptReprintTemplate;
  readonly useRounding: boolean;
  readonly copyEnabled: boolean;
}

export interface ReceiptItemInput {
  readonly name: string;
  readonly quantity: number;
  readonly unitPrice: number;
  readonly amount: number;
  readonly lineNote?: string;
  readonly lineDiscount?: number;
}

export interface ReceiptPaymentInput {
  readonly method: PaymentMethodCode;
  readonly amount: number;
  readonly change?: number;
  readonly evidenceRef?: string;
  readonly gatewayPaymentId?: string;
  readonly referenceNo?: string;
  readonly manualChannelId?: string;
  readonly paymentLabel?: string;
}

export interface ReceiptSaleInput {
  readonly receiptNo: string;
  readonly soldAt: string | number | Date;
  readonly cashier: string;
  readonly lines: readonly ReceiptItemInput[];
  readonly subtotal: number;
  readonly vatRate: number;
  readonly vatAmount: number;
  readonly totalAmount: number;
  readonly roundingDelta?: number;
  readonly payments: readonly ReceiptPaymentInput[];
  readonly copyCount?: number;
}

type PaymentDisplayInput = {
  readonly method: string;
  readonly manualChannelId?: string;
  readonly paymentLabel?: string;
};

export const isLegacyManualPayment = (payment: PaymentDisplayInput): boolean =>
  payment.method === 'reference_manual' &&
  (!payment.manualChannelId ||
    payment.manualChannelId === LEGACY_MANUAL_CHANNEL_ID);

export const paymentDisplayLabel = (
  payment: PaymentDisplayInput,
  fallback: string,
): string =>
  payment.method === 'reference_manual' && payment.paymentLabel?.trim()
    ? payment.paymentLabel.trim()
    : fallback;

export const shouldDisplayPaymentEvidence = (
  payment: PaymentDisplayInput,
): boolean =>
  payment.method === 'qr_personal' || isLegacyManualPayment(payment);

export const normalizeZReportPayments = (
  rows: readonly DashboardSalesPaymentRow[],
): DashboardSalesPaymentRow[] => {
  const required = [
    { method: 'cash', label: 'เงินสด' },
    { method: 'qr_personal', label: 'QR พร้อมเพย์' },
    {
      method: 'reference_manual',
      label: LEGACY_MANUAL_PAYMENT_LABEL,
    },
    { method: 'qr_gateway', label: 'QR นิติ-SCB' },
  ];
  const presentMethods = new Set(rows.map((row) => row.method));
  return [
    ...rows,
    ...required
      .filter((row) => !presentMethods.has(row.method))
      .map((row) => ({ ...row, amount: 0 })),
  ];
};

export interface ReceiptRenderInput {
  readonly template: ReceiptTemplate;
  readonly sale: ReceiptSaleInput;
}

export interface ReceiptRenderResult {
  readonly content: string;
  readonly lines: string[];
}

export type DomainDocumentType = 'sale' | 'return' | 'shift';

export interface UlidInput {
  timestamp?: number | Date;
  random?: () => number;
}

export interface BuildSortableIdInput {
  namespace: string;
  sequence: number;
  timestamp?: number | Date;
  random?: () => number;
}

export type StockPolicyMode =
  'allow' | 'warn' | 'block_online_only' | 'manager_override';

export type StockPolicySeverity =
  'none' | 'warning' | 'blocked' | 'requires_manager';

export interface StockPolicyDecision {
  allowed: boolean;
  severity: StockPolicySeverity;
  requiresManager: boolean;
  message: string;
}

export interface StockPolicyInput {
  mode: StockPolicyMode;
  currentStock: number;
  delta: number;
  isOnline: boolean;
  actorIsManager: boolean;
}

export interface BillRoundingInput {
  totalAmount: number;
  roundingStep: number;
}

const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

const clampToSafeTimestamp = (value: number): bigint => {
  if (!Number.isFinite(value)) {
    return 0n;
  }
  if (value <= 0) {
    return 0n;
  }
  return BigInt(Math.floor(value));
};

const encodeBase32 = (value: bigint, width: number): string => {
  let remaining = value;
  const chars: string[] = new Array(width);
  for (let index = width - 1; index >= 0; index -= 1) {
    const digit = Number(remaining & 31n);
    chars[index] = ULID_ALPHABET[digit];
    remaining >>= 5n;
  }
  return chars.join('');
};

const randomUlidSegment = (random: () => number, width: number): string => {
  let output = '';
  for (let i = 0; i < width; i += 1) {
    const slot = Math.floor(random() * 32);
    output += ULID_ALPHABET[Math.min(31, Math.max(0, slot))];
  }
  return output;
};

const roundHalfUp = (value: number, precision = 0): number => {
  const scale = 10 ** precision;
  if (scale === 1) {
    return Math.trunc(value >= 0 ? value + 0.5 : value - 0.5);
  }
  const scaled = value * scale;
  return Math.trunc(value >= 0 ? scaled + 0.5 : scaled - 0.5) / scale;
};

export function generateSortableUlid(input: UlidInput = {}): string {
  const timestamp = clampToSafeTimestamp(
    input.timestamp instanceof Date
      ? input.timestamp.getTime()
      : (input.timestamp ?? Date.now()),
  );
  const random = input.random ?? Math.random;
  return `${encodeBase32(timestamp, 10)}${randomUlidSegment(random, 16)}`;
}

export function buildSortableId(input: BuildSortableIdInput): string {
  const seq = Math.max(0, Math.trunc(input.sequence));
  const ts = input.timestamp ?? Date.now();
  const time = new Date(ts).toISOString();
  return `${input.namespace}|${time}|${seq.toString().padStart(6, '0')}|${generateSortableUlid(
    {
      timestamp: ts,
      random: input.random,
    },
  )}`;
}

export function calculateVatBreakdown(
  grossSatang: number,
  vatRate: number,
  inclusive: boolean,
): VatBreakdown {
  if (inclusive) {
    const denominator = 100 + vatRate;
    const netSatang =
      denominator > 0
        ? roundHalfUp((grossSatang * 100) / denominator, 0)
        : grossSatang;
    const vatSatang = grossSatang - netSatang;
    return {
      netAmount: netSatang,
      vatAmount: vatSatang,
      grossAmount: grossSatang,
    };
  }

  const vatSatang = roundHalfUp((grossSatang * vatRate) / 100, 0);
  return {
    netAmount: grossSatang,
    vatAmount: vatSatang,
    grossAmount: grossSatang + vatSatang,
  };
}

export function roundCashAmount(satang: number, stepSatang: number): number {
  if (stepSatang <= 0) {
    return Math.trunc(satang);
  }
  const steps = Math.round(satang / stepSatang);
  return steps * stepSatang;
}

export function roundBillTotal(input: BillRoundingInput): BillRoundingResult {
  const rounded = roundCashAmount(input.totalAmount, input.roundingStep);
  return {
    roundedTotal: rounded,
    roundingDelta: rounded - input.totalAmount,
  };
}

const resolveReceiptLocale = (locale?: ReceiptLocale): ReceiptLocale =>
  locale ?? 'th';

const formatReceiptMoney = (satang: number): string => formatBaht(satang);

const formatReceiptQuantity = (value: number): string => {
  const scaled = Math.round(value * 100);
  const whole = (scaled - (scaled % 100)) / 100;
  const fraction = scaled % 100;
  if (fraction === 0) {
    return `${whole}`;
  }
  const trailing = `${fraction}`.padStart(2, '0');
  return `${whole}.${trailing}`;
};

const formatReceiptDate = (value: string | number | Date): string => {
  const parsed = new Date(value);
  const iso = Number.isNaN(parsed.getTime())
    ? '1970-01-01T00:00:00.000Z'
    : parsed.toISOString();
  const [date] = iso.split('T');
  return date;
};

const formatReceiptTime = (value: string | number | Date): string => {
  const parsed = new Date(value);
  const iso = Number.isNaN(parsed.getTime())
    ? '1970-01-01T00:00:00.000Z'
    : parsed.toISOString();
  return iso.slice(11, 16);
};

const centerLine = (value: string, width: number): string =>
  value.length >= width
    ? value
    : ' '.repeat(Math.floor((width - value.length) / 2)) +
      value +
      ' '.repeat(Math.ceil((width - value.length) / 2));

const dividerLine = (template: ReceiptTemplate): string =>
  template.dividerChar.repeat(template.width);

const padRightRow = (left: string, right: string, width: number): string => {
  const spaceCount = Math.max(1, width - left.length - right.length);
  return `${left}${' '.repeat(spaceCount)}${right}`;
};

const pickCopyCount = (input: ReceiptSaleInput): number =>
  input.copyCount === undefined ? 1 : Math.max(1, Math.trunc(input.copyCount));

const labelsByLocale: Record<
  ReceiptLocale,
  {
    receiptTitle: [string, string];
    taxPrefix: string;
    subtotal: string;
    total: string;
    reprint: (count: number) => string;
    cashLabel: string;
    qrPersonalLabel: string;
    qrGatewayLabel: string;
    qrTransferLabel: string;
    changeLabel: string;
    evidenceLabel: string;
    gatewayPaymentIdLabel: string;
    settlementLabel: string;
    receiptNoLabel: string;
    dateLabel: string;
    timeLabel: string;
    cashierLabel: string;
    vatLabel: string;
    footerThanks: readonly string[];
  }
> = {
  th: {
    receiptTitle: ['ใบเส็จรับเงิน', 'RECEIPT'],
    taxPrefix: 'VAT',
    subtotal: 'Subtotal (ก่อน VAT)',
    total: 'Total (รวม VAT)',
    reprint: (copyCount) => `*** สำเนา / COPY (พิมพ์ครั้งที่ ${copyCount}) ***`,
    cashLabel: 'ชำระเงินสด',
    qrPersonalLabel: 'พร้อมเพย์ส่วนบุคคล',
    qrGatewayLabel: 'พร้อมเพย์ Gateway',
    qrTransferLabel: 'โอนแนะนำ',
    changeLabel: 'เงินทอน',
    evidenceLabel: 'หลักฐานการโอน: แนบในระบบ',
    gatewayPaymentIdLabel: 'Gateway Payment ID',
    settlementLabel: 'Settlement:',
    receiptNoLabel: 'เลขที่',
    dateLabel: 'วันที่',
    timeLabel: 'เวลา',
    cashierLabel: 'แคชเชียร์',
    vatLabel: 'VAT',
    footerThanks: ['ขอบคุณที่ใช้บริการ', 'Thank you'],
  },
  en: {
    receiptTitle: ['RECEIPT', 'RECEIPT'],
    taxPrefix: 'VAT',
    subtotal: 'Subtotal (Before VAT)',
    total: 'Total (incl VAT)',
    reprint: (copyCount) => `*** COPY / REPRINT (PRINT #${copyCount}) ***`,
    cashLabel: 'Cash',
    qrPersonalLabel: 'QR Personal',
    qrGatewayLabel: 'QR Gateway',
    qrTransferLabel: 'Reference Transfer',
    changeLabel: 'Change',
    evidenceLabel: 'Evidence attached',
    gatewayPaymentIdLabel: 'Gateway Payment ID',
    settlementLabel: 'Settlement:',
    receiptNoLabel: 'Receipt No.',
    dateLabel: 'Date',
    timeLabel: 'Time',
    cashierLabel: 'Cashier',
    vatLabel: 'VAT',
    footerThanks: ['Thank you', 'Thank you'],
  },
};

const paymentLabel = (
  method: PaymentMethodCode,
  template: ReceiptTemplate,
): string => {
  if (template.labels.paymentLabels[method]) {
    return template.labels.paymentLabels[method];
  }
  if (method === 'cash') {
    return template.labels.paymentLabels.cash;
  }
  if (method === 'qr_personal') {
    return template.labels.paymentLabels.qr_personal;
  }
  return template.labels.paymentLabels.card;
};

export const createDefaultReceiptTemplate = (params: {
  readonly locale?: ReceiptLocale;
  readonly settings?: Pick<SystemSettings, 'receiptHeader' | 'receiptFooter'>;
  readonly shopName?: string;
  readonly branchName?: string;
  readonly branchAddress?: string;
  readonly taxId?: string;
  readonly phone?: string;
  readonly width?: number;
  readonly dividerChar?: string;
  readonly copyTemplate?: string;
  readonly includeRounding?: boolean;
}): ReceiptTemplate => {
  const locale = resolveReceiptLocale(params.locale);
  const labels = labelsByLocale[locale];
  const width = Math.max(20, Math.trunc(params.width ?? 35));
  const dividerChar = params.dividerChar ?? '─';
  const addressLines = [params.branchAddress].filter(
    (line): line is string => !!line,
  );
  const shopLine = params.shopName ?? DEFAULT_SYSTEM_SETTINGS.shopName;
  const branchLine = params.branchName ? `สาขา ${params.branchName}` : '';
  const taxLine = params.taxId ? `Tax ID: ${params.taxId}` : 'Tax ID: -';
  const phoneLine = params.phone ? `โทร ${params.phone}` : undefined;
  const customHeader =
    params.settings?.receiptHeader?.trim()?.split('\n').filter(Boolean) ?? [];
  const customFooter =
    params.settings?.receiptFooter?.trim()?.split('\n').filter(Boolean) ?? [];

  const paymentLabels: ReceiptTemplate['labels']['paymentLabels'] = {
    cash: labels.cashLabel,
    qr_gateway: labels.qrGatewayLabel,
    qr_scb: labels.qrGatewayLabel,
    qr_personal: labels.qrPersonalLabel,
    reference_manual: labels.qrTransferLabel,
    qr: labels.qrGatewayLabel,
    card: labels.qrGatewayLabel,
    transfer: labels.qrTransferLabel,
  };

  return {
    locale,
    width,
    dividerChar,
    header: {
      titlePrimary: labels.receiptTitle[0],
      titleSecondary: labels.receiptTitle[1],
      logoLine: 'LOGO',
      shopLine: shopLine || 'POS Shop',
      branchLine,
      addressLines: [...customHeader, ...addressLines],
      taxLine,
      phoneLine,
    },
    footer: {
      lines: customFooter.length > 0 ? customFooter : labels.footerThanks,
    },
    labels: {
      receiptNoLabel: `${labels.receiptNoLabel}:`,
      dateLabel: `${labels.dateLabel}:`,
      timeLabel: `${labels.timeLabel}:`,
      cashierLabel: `${labels.cashierLabel}:`,
      subtotalLabel: labels.subtotal,
      vatLabel: labels.taxPrefix,
      totalLabel: labels.total,
      paymentLabels,
      changeLabel: labels.changeLabel,
      evidenceLabel: labels.evidenceLabel,
      gatewayPaymentIdLabel: labels.gatewayPaymentIdLabel,
      settlementLabel: labels.settlementLabel,
    },
    reprint: {
      bannerTemplate: params.copyTemplate ?? labels.reprint(2),
      lineTemplate: labelsByLocale[locale].reprint(2),
    },
    useRounding: params.includeRounding ?? true,
    copyEnabled: true,
  };
};

const renderItemLine = (
  _template: ReceiptTemplate,
  item: ReceiptItemInput,
): string[] => {
  const lines = [`${item.name}${item.lineNote ? ` ${item.lineNote}` : ''}`];
  const detail = `${formatReceiptMoney(item.unitPrice)} × ${formatReceiptQuantity(
    item.quantity,
  )} = ${formatReceiptMoney(item.amount)}`;
  lines.push(`  ${detail}`);
  if (item.lineDiscount && item.lineDiscount > 0) {
    lines.push(`  -${formatReceiptMoney(item.lineDiscount)}`);
  }
  return lines;
};

const renderPaymentLine = (
  template: ReceiptTemplate,
  payment: ReceiptPaymentInput,
): string[] => {
  const resolvedPaymentLabel = paymentDisplayLabel(
    payment,
    paymentLabel(payment.method, template),
  );
  const lines = [
    padRightRow(
      resolvedPaymentLabel,
      formatReceiptMoney(payment.amount),
      template.width,
    ),
  ];
  if (payment.method === 'cash' && typeof payment.change === 'number') {
    lines.push(
      padRightRow(
        template.labels.changeLabel,
        formatReceiptMoney(payment.change),
        template.width,
      ),
    );
  }
  if (payment.evidenceRef && shouldDisplayPaymentEvidence(payment)) {
    lines.push(template.labels.evidenceLabel);
    lines.push(`  ${payment.evidenceRef}`);
  }
  if (payment.method === 'qr' || payment.method === 'card') {
    if (payment.gatewayPaymentId) {
      lines.push(`${template.labels.gatewayPaymentIdLabel}:`);
      lines.push(`  ${payment.gatewayPaymentId}`);
    }
    lines.push(`  ${template.labels.settlementLabel} pending`);
  }
  if (payment.method === 'reference_manual' && payment.referenceNo) {
    lines.push(`${template.labels.evidenceLabel}: ${payment.referenceNo}`);
  }
  return lines;
};

export function createReceiptRenderer(
  template: ReceiptTemplate,
): (input: Omit<ReceiptRenderInput, 'template'>) => ReceiptRenderResult {
  return (input) => renderReceipt({ template, sale: input.sale });
}

export function renderReceipt(input: ReceiptRenderInput): ReceiptRenderResult {
  const { template, sale } = input;
  const copyCount = pickCopyCount(sale);
  const lines: string[] = [];

  if (template.copyEnabled && copyCount > 1) {
    lines.push(dividerLine(template));
    lines.push(
      centerLine(
        template.reprint.lineTemplate.replace('2', `${copyCount}`),
        template.width,
      ),
    );
    lines.push(dividerLine(template));
  }

  lines.push(dividerLine(template));
  lines.push(centerLine(template.header.logoLine, template.width));
  lines.push(centerLine(template.header.titlePrimary, template.width));
  lines.push(centerLine(template.header.titleSecondary, template.width));
  lines.push(centerLine(dividerLine(template), template.width));
  lines.push(centerLine(template.header.shopLine, template.width));
  if (template.header.branchLine) {
    lines.push(centerLine(template.header.branchLine, template.width));
  }
  for (const line of template.header.addressLines) {
    lines.push(centerLine(line, template.width));
  }
  lines.push(centerLine(template.header.taxLine, template.width));
  if (template.header.phoneLine) {
    lines.push(centerLine(template.header.phoneLine, template.width));
  }
  lines.push(dividerLine(template));

  lines.push(`${template.labels.receiptNoLabel} ${sale.receiptNo}`);
  lines.push(
    `${template.labels.dateLabel} ${formatReceiptDate(sale.soldAt)} ${template.labels.timeLabel} ${formatReceiptTime(sale.soldAt)}`,
  );
  lines.push(`${template.labels.cashierLabel} ${sale.cashier}`);
  lines.push(dividerLine(template));

  for (const item of sale.lines) {
    lines.push(...renderItemLine(template, item));
  }
  lines.push(dividerLine(template));

  lines.push(
    padRightRow(
      template.labels.subtotalLabel,
      formatReceiptMoney(sale.subtotal),
      template.width,
    ),
  );
  lines.push(
    padRightRow(
      `${template.labels.vatLabel} ${Math.round(sale.vatRate * 100)}%`,
      formatReceiptMoney(sale.vatAmount),
      template.width,
    ),
  );
  if (template.useRounding && sale.roundingDelta && sale.roundingDelta !== 0) {
    lines.push(
      padRightRow(
        `Rounding`,
        formatReceiptMoney(sale.roundingDelta),
        template.width,
      ),
    );
  }
  lines.push(
    padRightRow(
      template.labels.totalLabel,
      formatReceiptMoney(sale.totalAmount),
      template.width,
    ),
  );
  lines.push(dividerLine(template));

  for (const payment of sale.payments) {
    lines.push(...renderPaymentLine(template, payment));
  }
  lines.push(dividerLine(template));

  for (const line of template.footer.lines) {
    lines.push(centerLine(line, template.width));
  }
  lines.push(dividerLine(template));

  return {
    content: lines.join('\n'),
    lines,
  };
}

export function canApplyStockAdjustment(
  input: StockPolicyInput,
): StockPolicyDecision {
  if (input.mode === 'allow') {
    return {
      allowed: true,
      severity: 'none',
      requiresManager: false,
      message: 'Stock policy allows all local adjustments',
    };
  }

  if (input.mode === 'warn' && input.currentStock + input.delta < 0) {
    return {
      allowed: true,
      severity: 'warning',
      requiresManager: false,
      message: 'Stock will become negative; approval is recommended',
    };
  }

  if (
    input.mode === 'block_online_only' &&
    input.currentStock + input.delta < 0
  ) {
    if (!input.isOnline) {
      return {
        allowed: false,
        severity: 'blocked',
        requiresManager: false,
        message: 'Offline stock adjustment is blocked; come online to continue',
      };
    }

    return {
      allowed: true,
      severity: 'none',
      requiresManager: false,
      message:
        'Online mode allows negative stock adjustment with sync tracking',
    };
  }

  if (
    input.mode === 'manager_override' &&
    input.currentStock + input.delta < 0
  ) {
    if (input.actorIsManager) {
      return {
        allowed: true,
        severity: 'none',
        requiresManager: false,
        message: 'Manager override is active',
      };
    }

    return {
      allowed: false,
      severity: 'requires_manager',
      requiresManager: true,
      message: 'Manager approval required for negative stock adjustment',
    };
  }

  return {
    allowed: true,
    severity: 'none',
    requiresManager: false,
    message: 'Stock adjustment accepted',
  };
}

export function getVisibleBranchIds(
  user: Pick<UserAccount, 'role' | 'branchIds'>,
  allBranchIds: string[],
): string[] {
  if (user.role === 'owner') {
    return allBranchIds;
  }

  return allBranchIds.filter((branchId) => user.branchIds.includes(branchId));
}

export function resolvePermission(
  user: Pick<UserAccount, 'role' | 'permissionOverrides'> | null,
  action: PermissionAction,
  settings: SystemSettings = DEFAULT_SYSTEM_SETTINGS,
): PermissionResult {
  if (!user) {
    return { allowed: false, reason: 'user_missing' };
  }

  const override = resolvePermissionOverride(user.permissionOverrides, action);
  if (override) {
    return {
      allowed: override.effect === 'allow',
      cap: override.cap,
      reason: override.effect === 'deny' ? 'override_denied' : undefined,
    };
  }

  const result = roleDefaults(user.role, settings)[action];
  return result
    ? result
    : {
        allowed: false,
        reason: 'role_disallowed',
      };
}

export function canEditMasterData(
  user: Pick<UserAccount, 'role' | 'permissionOverrides'> | null,
): boolean {
  return resolvePermission(user, 'dashboard.masterData.write').allowed;
}

export function canEditPromotions(
  user: Pick<UserAccount, 'role' | 'permissionOverrides'> | null,
): boolean {
  return resolvePermission(user, 'dashboard.promotions.write').allowed;
}

export function canEditSystemConfig(
  user: Pick<UserAccount, 'role' | 'permissionOverrides'> | null,
): boolean {
  return resolvePermission(user, 'dashboard.system.write').allowed;
}

function roleDefaults(
  role: Role,
  settings: SystemSettings,
): Partial<Record<PermissionAction, PermissionResult>> {
  const discountCaps = {
    cashierBill: settings.cashierBillDiscountPct,
    managerBill: settings.managerBillDiscountPct,
    ownerBill: settings.ownerBillDiscountPct,
  };

  switch (role) {
    case 'staff':
      return {
        'dashboard.branches.read': { allowed: true },
      };
    case 'cashier':
      return {
        'dashboard.branches.read': { allowed: true },
        'sale.line-discount': {
          allowed: false,
        },
        'sale.bill-discount': {
          allowed: true,
          cap: discountCaps.cashierBill,
        },
        'shift.close-self': { allowed: true },
        reprint: { allowed: true },
      };
    case 'manager':
      return {
        'dashboard.branches.read': { allowed: true },
        'dashboard.masterData.write': { allowed: true },
        'dashboard.promotions.write': { allowed: true },
        'sale.line-discount': {
          allowed: true,
          cap: 100,
        },
        'sale.bill-discount': {
          allowed: true,
          cap: discountCaps.managerBill,
        },
        'sale.void': { allowed: true },
        'sale.recall-others': { allowed: true },
        'stock.adjust': { allowed: true },
        'stock.in': { allowed: true },
        'transfer.create': { allowed: true },
        'transfer.receive': { allowed: true },
        'transfer.cancel-post-sent': { allowed: true },
        'shift.close-self': { allowed: true },
        'shift.close-other': { allowed: true },
        reprint: { allowed: true },
      };
    case 'owner':
      return {
        'dashboard.branches.read': { allowed: true },
        'dashboard.masterData.write': { allowed: true },
        'dashboard.promotions.write': { allowed: true },
        'dashboard.system.write': { allowed: true },
        'sale.line-discount': {
          allowed: true,
          cap: 100,
        },
        'sale.bill-discount': {
          allowed: true,
          cap: discountCaps.ownerBill,
        },
        'sale.void': { allowed: true },
        'sale.recall-others': { allowed: true },
        'stock.adjust': { allowed: true },
        'stock.in': { allowed: true },
        'transfer.create': { allowed: true },
        'transfer.receive': { allowed: true },
        'transfer.cancel-post-sent': { allowed: true },
        'shift.close-self': { allowed: true },
        'shift.close-other': { allowed: true },
        reprint: { allowed: true },
      };
  }
}

export function calculateStockPolicySeverity(
  input: StockPolicyInput,
): StockPolicyDecision {
  return canApplyStockAdjustment(input);
}

export interface PromotionLineInput {
  productId: string;
  quantity: number;
  unitPrice: number;
}

export interface PromotionLineDiscount {
  productId: string;
  quantity: number;
  beforeAmount: number;
  afterAmount: number;
  discount: number;
}

export interface PromotionCandidate {
  promotion: Promotion;
  discountAmount: number;
  lineDiscounts: PromotionLineDiscount[];
}

export interface PromotionEvaluationInput {
  branchId: string;
  subtotal: number;
  now: string;
  lines: readonly PromotionLineInput[];
}

export interface PromotionEvaluationResult {
  candidates: PromotionCandidate[];
  best: PromotionCandidate | null;
  totalDiscount: number;
}

const toMoney2 = (value: number): number => roundHalfUp(value, 0);

const lineAmount = (line: PromotionLineInput): number =>
  toMoney2(line.quantity * line.unitPrice);

const scopedLines = (
  promotion: Promotion,
  lines: readonly PromotionLineInput[],
): PromotionLineInput[] => {
  if (!promotion.productIds || promotion.productIds.length === 0) {
    return [...lines];
  }

  const scope = new Set(promotion.productIds);
  return lines.filter((line) => scope.has(line.productId));
};

const evaluateFixedPrice = (
  promotion: Promotion,
  lines: readonly PromotionLineInput[],
): PromotionCandidate | null => {
  const targetPrice = Math.max(0, promotion.value);
  if (targetPrice <= 0) {
    return null;
  }

  const lineDiscounts: PromotionLineDiscount[] = [];
  for (const line of lines) {
    const quantity = Math.max(0, line.quantity);
    const beforeAmount = toMoney2(line.unitPrice * quantity);
    const afterAmount = toMoney2(targetPrice * quantity);

    if (afterAmount >= beforeAmount) {
      continue;
    }

    lineDiscounts.push({
      productId: line.productId,
      quantity,
      beforeAmount,
      afterAmount,
      discount: toMoney2(beforeAmount - afterAmount),
    });
  }

  const discountAmount = toMoney2(
    lineDiscounts.reduce((sum, item) => sum + item.discount, 0),
  );
  return discountAmount > 0
    ? { promotion, discountAmount, lineDiscounts }
    : null;
};

const evaluatePercentOff = (
  promotion: Promotion,
  lines: readonly PromotionLineInput[],
): PromotionCandidate | null => {
  const discountRate = promotion.value / 100;
  if (!Number.isFinite(discountRate) || discountRate <= 0) {
    return null;
  }

  const lineDiscounts = lines
    .map((line) => {
      const beforeAmount = lineAmount(line);
      const discount = toMoney2(beforeAmount * discountRate);
      if (discount <= 0) {
        return null;
      }
      return {
        productId: line.productId,
        quantity: Math.max(0, line.quantity),
        beforeAmount,
        afterAmount: toMoney2(beforeAmount - discount),
        discount,
      } as PromotionLineDiscount;
    })
    .filter((entry): entry is PromotionLineDiscount => entry !== null);

  const discountAmount = toMoney2(
    lineDiscounts.reduce((sum, item) => sum + item.discount, 0),
  );
  return discountAmount > 0
    ? { promotion, discountAmount, lineDiscounts }
    : null;
};

const evaluateQtyTier = (
  promotion: Promotion,
  lines: readonly PromotionLineInput[],
): PromotionCandidate | null => {
  const minQty = Math.max(0, Math.floor(promotion.minQty ?? 0));
  const discountStep = Math.max(0, toMoney2(promotion.value));
  if (minQty <= 0 || discountStep <= 0) {
    return null;
  }

  const eligibleQty = lines.reduce(
    (sum, line) => sum + Math.max(0, line.quantity),
    0,
  );
  const availableTiers = Math.floor(eligibleQty / minQty);
  const usableTiers =
    promotion.perBillLimit === undefined
      ? availableTiers
      : Math.min(
          availableTiers,
          Math.max(0, Math.floor(promotion.perBillLimit)),
        );
  const eligibleAmount = toMoney2(
    lines.reduce((sum, line) => sum + lineAmount(line), 0),
  );
  let remainingDiscount = Math.min(
    eligibleAmount,
    toMoney2(usableTiers * discountStep),
  );
  const lineDiscounts: PromotionLineDiscount[] = [];

  for (const line of lines) {
    if (remainingDiscount <= 0) break;
    const beforeAmount = lineAmount(line);
    const discount = Math.min(beforeAmount, remainingDiscount);
    if (discount <= 0) continue;
    remainingDiscount = toMoney2(remainingDiscount - discount);
    lineDiscounts.push({
      productId: line.productId,
      quantity: line.quantity,
      beforeAmount,
      afterAmount: toMoney2(beforeAmount - discount),
      discount,
    });
  }

  const discountAmount = toMoney2(
    lineDiscounts.reduce((sum, item) => sum + item.discount, 0),
  );
  return discountAmount > 0
    ? { promotion, discountAmount, lineDiscounts }
    : null;
};

const evaluateBogo = (
  promotion: Promotion,
  lines: readonly PromotionLineInput[],
): PromotionCandidate | null => {
  const buyQty = Math.floor(promotion.minQty ?? 0);
  const freeQty = Math.floor(promotion.value);
  if (buyQty < 2 || freeQty < 1) {
    return null;
  }

  const totalQty = lines.reduce(
    (sum, line) => sum + Math.max(0, Math.floor(line.quantity)),
    0,
  );
  const pairs = Math.floor(totalQty / buyQty);
  const maxFreeUnits = pairs * freeQty;
  if (maxFreeUnits <= 0) {
    return null;
  }

  const cap =
    promotion.perBillLimit === undefined
      ? Number.POSITIVE_INFINITY
      : Math.max(0, Math.floor(promotion.perBillLimit));
  let remainingFreeUnits = Math.min(maxFreeUnits, cap);

  const eligible = [...lines].sort((a, b) => b.unitPrice - a.unitPrice);
  const lineDiscounts: PromotionLineDiscount[] = [];
  for (const line of eligible) {
    if (remainingFreeUnits <= 0) {
      break;
    }

    const lineQty = Math.floor(Math.max(0, line.quantity));
    const freeUnits = Math.min(lineQty, remainingFreeUnits);
    if (freeUnits <= 0) {
      continue;
    }

    const beforeAmount = lineAmount(line);
    const discount = toMoney2(line.unitPrice * freeUnits);
    const actualDiscount = Math.min(beforeAmount, discount);

    remainingFreeUnits -= freeUnits;
    lineDiscounts.push({
      productId: line.productId,
      quantity: lineQty,
      beforeAmount,
      afterAmount: toMoney2(beforeAmount - actualDiscount),
      discount: actualDiscount,
    });
  }

  const discountAmount = toMoney2(
    lineDiscounts.reduce((sum, item) => sum + item.discount, 0),
  );
  return discountAmount > 0
    ? { promotion, discountAmount, lineDiscounts }
    : null;
};

const evaluateBillPercent = (
  promotion: Promotion,
  subtotal: number,
): PromotionCandidate | null => {
  const discountRate = promotion.value / 100;
  if (!Number.isFinite(discountRate) || discountRate <= 0 || subtotal <= 0) {
    return null;
  }

  const rawDiscount = toMoney2(subtotal * discountRate);
  const cappedDiscount =
    promotion.perBillLimit === undefined
      ? rawDiscount
      : Math.min(rawDiscount, promotion.perBillLimit);

  return cappedDiscount > 0
    ? {
        promotion,
        discountAmount: cappedDiscount,
        lineDiscounts: [
          {
            productId: '__bill__',
            quantity: 1,
            beforeAmount: toMoney2(subtotal),
            afterAmount: toMoney2(subtotal - cappedDiscount),
            discount: cappedDiscount,
          },
        ],
      }
    : null;
};

export function evaluatePromotion(
  promotion: Promotion,
  input: PromotionEvaluationInput,
): PromotionCandidate | null {
  const lines = scopedLines(promotion, input.lines);
  const scopedQty = lines.reduce(
    (sum, line) => sum + Math.max(0, line.quantity),
    0,
  );

  if (
    !isPromotionEligible({
      promotion,
      branchId: input.branchId,
      subtotal: input.subtotal,
      quantity: scopedQty,
      now: input.now,
    })
  ) {
    return null;
  }

  if (promotion.type === 'bill_percent') {
    return evaluateBillPercent(promotion, input.subtotal);
  }

  if (lines.length === 0) {
    return null;
  }

  if (promotion.type === 'fixed_price') {
    return evaluateFixedPrice(promotion, lines);
  }
  if (promotion.type === 'percent_off') {
    return evaluatePercentOff(promotion, lines);
  }
  if (promotion.type === 'qty_tier') {
    return evaluateQtyTier(promotion, lines);
  }
  if (promotion.type === 'bogo') {
    return evaluateBogo(promotion, lines);
  }

  return null;
}

export function chooseBestPromotion(
  candidates: readonly PromotionCandidate[],
): PromotionCandidate | null {
  if (candidates.length === 0) {
    return null;
  }

  return candidates.reduce<PromotionCandidate | null>((best, current) => {
    if (!best) {
      return current;
    }
    if (current.discountAmount !== best.discountAmount) {
      return current.discountAmount > best.discountAmount ? current : best;
    }
    return current.promotion.id < best.promotion.id ? current : best;
  }, null);
}

export function evaluatePromotions(
  promotions: readonly Promotion[],
  input: PromotionEvaluationInput,
): PromotionEvaluationResult {
  const candidates = promotions
    .map((promotion) => evaluatePromotion(promotion, input))
    .filter((entry): entry is PromotionCandidate => entry !== null)
    .sort(
      (a, b) =>
        b.discountAmount - a.discountAmount ||
        a.promotion.id.localeCompare(b.promotion.id),
    );

  const best = chooseBestPromotion(candidates);
  return {
    candidates,
    best,
    totalDiscount: best?.discountAmount ?? 0,
  };
}

export function convertUnitQuantity(
  product: ProductCatalogItem,
  productUnitId: string,
  quantity: number,
): number {
  const unit = product.units.find((entry) => entry.id === productUnitId);
  if (!unit) {
    throw new Error(`Unknown unit ${productUnitId} for product ${product.id}`);
  }

  return quantity * unit.ratio;
}

export function isPromotionEligible(input: {
  promotion: Promotion;
  branchId: string;
  subtotal: number;
  quantity: number;
  now: string;
}): boolean {
  const { promotion, branchId, subtotal, quantity, now } = input;
  if (!promotion.active) {
    return false;
  }
  if (
    promotion.branchIds.length > 0 &&
    !promotion.branchIds.includes(branchId)
  ) {
    return false;
  }
  if (promotion.startDate > now || promotion.endDate < now) {
    return false;
  }
  if (promotion.minBaht && subtotal < promotion.minBaht) {
    return false;
  }
  if (promotion.minQty && quantity < promotion.minQty) {
    return false;
  }

  return true;
}

export function validateStockAdjustment(
  deltaBaseUnits: number,
  maxAbsoluteWithoutApproval = 5,
): { allowed: boolean; requireApproval: boolean } {
  return {
    allowed: true,
    requireApproval: Math.abs(deltaBaseUnits) > maxAbsoluteWithoutApproval,
  };
}

export function applyMovingAverageCost(input: {
  currentQuantity: number;
  currentUnitCost: number;
  receivedQuantity: number;
  receivedUnitCost: number;
}): number {
  const totalQuantity = input.currentQuantity + input.receivedQuantity;
  if (totalQuantity <= 0) {
    return roundHalfUp(input.receivedUnitCost, 0);
  }

  const weightedCost =
    input.currentQuantity * input.currentUnitCost +
    input.receivedQuantity * input.receivedUnitCost;
  return roundHalfUp(weightedCost / totalQuantity, 0);
}

export type {
  AppliedPromo,
  Promotion as CheckoutPromotion,
  PromoRule,
} from './checkoutPromotions';
export {
  computeAppliedPromos,
  getActivePromos,
  mapDesktopPromotionRow,
} from './checkoutPromotions';
export { normalizeThaiPhone } from './phone';
